import {
  Boxes,
  Building2,
  CalendarDays,
  Inbox,
  KeyRound,
  LayoutDashboard,
  Mail,
  Receipt,
  SquareKanban,
  Users,
  type LucideIcon,
} from 'lucide-react';

import { ADMIN_ROLES, hasRole, SUPERADMIN_ROLES } from '@/features/auth/permissions';
import type { AppRole } from '@/features/auth/types';

export type NavItemStatus = 'available' | 'planned';

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** `planned` items are shown disabled until their module ships. */
  status: NavItemStatus;
  /** Roles allowed to see the item (UX only; the server enforces access). */
  roles?: readonly AppRole[];
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

/**
 * Single source of truth for primary navigation. Modules flip to
 * `available` when their route is registered in `app/router/routes.tsx`.
 */
export const primaryNavigation: NavSection[] = [
  {
    label: 'General',
    items: [
      { label: 'Panel general', to: '/dashboard', icon: LayoutDashboard, status: 'available' },
    ],
  },
  {
    label: 'Comercial',
    items: [
      { label: 'Productos SaaS', to: '/saas', icon: Boxes, status: 'available' },
      { label: 'Clientes', to: '/clientes', icon: Building2, status: 'available' },
    ],
  },
  {
    label: 'Finanzas',
    items: [
      {
        label: 'Facturación',
        to: '/facturacion',
        icon: Receipt,
        status: 'available',
        roles: ADMIN_ROLES,
      },
    ],
  },
  {
    label: 'Operaciones',
    items: [{ label: 'Trabajo', to: '/trabajo', icon: SquareKanban, status: 'available' }],
  },
  {
    label: 'Agenda',
    // Every collaborator; private calendars stay private through RLS.
    items: [{ label: 'Agenda', to: '/agenda', icon: CalendarDays, status: 'available' }],
  },
  {
    label: 'Correo',
    items: [
      { label: 'Correos', to: '/correos', icon: Mail, status: 'available' },
      {
        label: 'Bandejas',
        to: '/bandejas',
        icon: Inbox,
        status: 'available',
        roles: ADMIN_ROLES,
      },
    ],
  },
];

export const secondaryNavigation: NavItem[] = [
  {
    label: 'Usuarios',
    to: '/configuracion/usuarios',
    icon: Users,
    status: 'available',
    roles: SUPERADMIN_ROLES,
  },
  {
    label: 'Integraciones',
    to: '/configuracion/integraciones',
    icon: KeyRound,
    status: 'available',
    roles: SUPERADMIN_ROLES,
  },
];

export const allNavItems: NavItem[] = [
  ...primaryNavigation.flatMap((section) => section.items),
  ...secondaryNavigation,
];

export function isNavItemVisible(item: NavItem, role: AppRole | undefined): boolean {
  return hasRole(role, item.roles);
}

/** Finds the nav item that owns a pathname (exact or nested match). */
export function findActiveNavItem(pathname: string): NavItem | undefined {
  return allNavItems.find((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
}
